'use client'

import { Input } from '@/components/forms/inputs/text/Input'
import { Badge } from '@/components/ui/common/Badge'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils/common'
import {
  Award,
  ChevronRight,
  DollarSign,
  ExternalLink,
  Globe,
  GraduationCap,
  Heart,
  Mail,
  MapPin,
  Phone,
  Search,
  Star,
  TrendingUp,
  Users,
  School
} from 'lucide-react'
import { useState } from 'react'

interface University {
  id: number
  name: string
  shortName: string
  type: 'Publiczna' | 'Prywatna'
  city: string
  region: string
  rating: number
  studentsCount: number
  foundedYear: number
  description: string
  faculties: string[]
  popularFields: string[]
  tuitionFee: string
  dormitoryAvailable: boolean
  scholarships: string[]
  contact: {
    phone: string
    email: string
    website: string
  }
  advantages: string[]
  employmentRate: number
  internationalPrograms: boolean
  isFavorite: boolean
}

export default function UniversityBase() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('Wszystkie')
  const [selectedRegion, setSelectedRegion] = useState('Wszystkie')
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null)

  const universities: University[] = [
    {
      id: 1,
      name: 'Uniwersytet Warszawski',
      shortName: 'UW',
      type: 'Publiczna',
      city: 'Warszawa',
      region: 'Mazowieckie',
      rating: 4.8,
      studentsCount: 45000,
      foundedYear: 1816,
      description:
        'Najstarsza i największa uczelnia w Polsce, oferująca szeroką gamę kierunków humanistycznych, społecznych i ścisłych.',
      faculties: [
        'Wydział Prawa i Administracji',
        'Wydział Psychologii',
        'Wydział Matematyki, Informatyki i Mechaniki',
        'Wydział Fizyki',
        'Wydział Biologii',
        'Wydział Filozofii'
      ],
      popularFields: ['Prawo', 'Psychologia', 'Informatyka', 'Matematyka', 'Biologia'],
      tuitionFee: 'Bezpłatne (studia stacjonarne)',
      dormitoryAvailable: true,
      scholarships: ['Stypendium socjalne', 'Stypendium naukowe', 'Stypendium rektora'],
      contact: {
        phone: '+48 22 552 00 00',
        email: 'info@uw.edu.pl',
        website: 'www.uw.edu.pl'
      },
      advantages: [
        'Prestiżowa uczelnia z długą tradycją',
        'Szeroka oferta kierunków',
        'Doskonała kadra naukowa',
        'Lokalizacja w centrum Warszawy'
      ],
      employmentRate: 92,
      internationalPrograms: true,
      isFavorite: true
    },
    {
      id: 2,
      name: 'Politechnika Warszawska',
      shortName: 'PW',
      type: 'Publiczna',
      city: 'Warszawa',
      region: 'Mazowieckie',
      rating: 4.7,
      studentsCount: 32000,
      foundedYear: 1915,
      description:
        'Wiodąca uczelnia techniczna w Polsce, kształcąca inżynierów w najnowocześniejszych technologiach.',
      faculties: [
        'Wydział Elektroniki i Technik Informacyjnych',
        'Wydział Mechaniczny Energetyki i Lotnictwa',
        'Wydział Budownictwa, Mechaniki i Petrochemii',
        'Wydział Chemiczny',
        'Wydział Architektury'
      ],
      popularFields: [
        'Informatyka',
        'Automatyka i Robotyka',
        'Budownictwo',
        'Architektura'
      ],
      tuitionFee: 'Bezpłatne (studia stacjonarne)',
      dormitoryAvailable: true,
      scholarships: [
        'Stypendium socjalne',
        'Stypendium naukowe',
        'Stypendium za osiągnięcia'
      ],
      contact: {
        phone: '+48 22 234 72 00',
        email: 'info@pw.edu.pl',
        website: 'www.pw.edu.pl'
      },
      advantages: [
        'Najlepsza uczelnia techniczna w Polsce',
        'Współpraca z przemysłem',
        'Nowoczesne laboratoria',
        'Wysokie zatrudnienie absolwentów'
      ],
      employmentRate: 96,
      internationalPrograms: true,
      isFavorite: false
    },
    {
      id: 3,
      name: 'Uniwersytet Jagielloński',
      shortName: 'UJ',
      type: 'Publiczna',
      city: 'Kraków',
      region: 'Małopolskie',
      rating: 4.9,
      studentsCount: 40000,
      foundedYear: 1364,
      description:
        'Najstarsza uczelnia w Polsce i jedna z najstarszych w Europie, o ogromnym prestiżu międzynarodowym.',
      faculties: [
        'Wydział Lekarski',
        'Wydział Prawa i Administracji',
        'Wydział Filozoficzny',
        'Wydział Matematyki i Informatyki',
        'Wydział Fizyki, Astronomii i Informatyki Stosowanej'
      ],
      popularFields: ['Medycyna', 'Prawo', 'Psychologia', 'Historia', 'Filozofia'],
      tuitionFee: 'Bezpłatne (studia stacjonarne)',
      dormitoryAvailable: true,
      scholarships: ['Stypendium socjalne', 'Stypendium naukowe', 'Stypendium ministra'],
      contact: {
        phone: '+48 12 663 10 00',
        email: 'info@uj.edu.pl',
        website: 'www.uj.edu.pl'
      },
      advantages: [
        'Najstarsza uczelnia w Polsce',
        'Międzynarodowy prestiż',
        'Piękny kampus w Krakowie',
        'Tradycja akademicka'
      ],
      employmentRate: 90,
      internationalPrograms: true,
      isFavorite: true
    },
    {
      id: 4,
      name: 'AGH Uniwersytet Nauki i Technologii',
      shortName: 'AGH',
      type: 'Publiczna',
      city: 'Kraków',
      region: 'Małopolskie',
      rating: 4.6,
      studentsCount: 28000,
      foundedYear: 1919,
      description:
        'Renomowana uczelnia techniczna specjalizująca się w naukach technicznych, górnictwie i informatyce.',
      faculties: [
        'Wydział Informatyki, Elektroniki i Telekomunikacji',
        'Wydział Inżynierii Mechanicznej i Robotyki',
        'Wydział Górnictwa i Geoinżynierii',
        'Wydział Energetyki i Paliw',
        'Wydział Matematyki Stosowanej'
      ],
      popularFields: ['Informatyka', 'Automatyka i Robotyka', 'Górnictwo', 'Energetyka'],
      tuitionFee: 'Bezpłatne (studia stacjonarne)',
      dormitoryAvailable: true,
      scholarships: ['Stypendium socjalne', 'Stypendium naukowe', 'Stypendium rektora'],
      contact: {
        phone: '+48 12 617 30 00',
        email: 'info@agh.edu.pl',
        website: 'www.agh.edu.pl'
      },
      advantages: [
        'Silna pozycja w rankingach technicznych',
        'Współpraca z przemysłem',
        'Nowoczesny kampus',
        'Tradycja górnicza'
      ],
      employmentRate: 94,
      internationalPrograms: true,
      isFavorite: false
    },
    {
      id: 5,
      name: 'SWPS Uniwersytet Humanistycznospołeczny',
      shortName: 'SWPS',
      type: 'Prywatna',
      city: 'Warszawa',
      region: 'Mazowieckie',
      rating: 4.3,
      studentsCount: 15000,
      foundedYear: 1996,
      description:
        'Wiodąca prywatna uczelnia w Polsce, specjalizująca się w naukach społecznych i humanistycznych.',
      faculties: [
        'Wydział Psychologii',
        'Wydział Nauk Humanistycznych i Społecznych',
        'Wydział Kultury i Sztuki',
        'Wydział Prawa i Komunikacji Społecznej'
      ],
      popularFields: ['Psychologia', 'Kulturoznawstwo', 'Dziennikarstwo', 'Prawo'],
      tuitionFee: '8 000 - 12 000 zł/rok',
      dormitoryAvailable: true,
      scholarships: ['Stypendium za wyniki w nauce', 'Stypendium socjalne'],
      contact: {
        phone: '+48 22 517 96 00',
        email: 'info@swps.edu.pl',
        website: 'www.swps.edu.pl'
      },
      advantages: [
        'Nowoczesne metody nauczania',
        'Międzynarodowe programy',
        'Praktyczne podejście',
        'Dobra współpraca z biznesem'
      ],
      employmentRate: 88,
      internationalPrograms: true,
      isFavorite: false
    },
    {
      id: 6,
      name: 'Politechnika Gdańska',
      shortName: 'PG',
      type: 'Publiczna',
      city: 'Gdańsk',
      region: 'Pomorskie',
      rating: 4.5,
      studentsCount: 25000,
      foundedYear: 1945,
      description:
        'Dynamicznie rozwijająca się uczelnia techniczna nad Bałtykiem, silna w obszarze technologii morskich.',
      faculties: [
        'Wydział Elektroniki, Telekomunikacji i Informatyki',
        'Wydział Mechaniczny',
        'Wydział Oceanotechniki i Okrętownictwa',
        'Wydział Architektury',
        'Wydział Chemiczny'
      ],
      popularFields: [
        'Informatyka',
        'Okrętownictwo',
        'Architektura',
        'Inżynieria środowiska'
      ],
      tuitionFee: 'Bezpłatne (studia stacjonarne)',
      dormitoryAvailable: true,
      scholarships: [
        'Stypendium socjalne',
        'Stypendium naukowe',
        'Stypendium za osiągnięcia'
      ],
      contact: {
        phone: '+48 58 347 10 00',
        email: 'info@pg.edu.pl',
        website: 'www.pg.edu.pl'
      },
      advantages: [
        'Specjalizacja w technologiach morskich',
        'Bliskość morza',
        'Współpraca z przemysłem stoczniowym',
        'Nowoczesne laboratoria'
      ],
      employmentRate: 91,
      internationalPrograms: true,
      isFavorite: false
    }
  ]

  const types = ['Wszystkie', 'Publiczna', 'Prywatna']
  const regions = [
    'Wszystkie',
    'Mazowieckie',
    'Małopolskie',
    'Pomorskie',
    'Śląskie',
    'Wielkopolskie'
  ]

  const filteredUniversities = universities.filter((university) => {
    const matchesSearch =
      university.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      university.city.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = selectedType === 'Wszystkie' || university.type === selectedType
    const matchesRegion =
      selectedRegion === 'Wszystkie' || university.region === selectedRegion
    return matchesSearch && matchesType && matchesRegion
  })

  if (selectedUniversity) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <button
              className="mb-4 flex items-center text-[#2db2b8] hover:text-[#2db2b8]/80"
              onClick={() => setSelectedUniversity(null)}
            >
              <ChevronRight className="mr-1 h-4 w-4 rotate-180 transform" />
              Powrót do listy uczelni
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {selectedUniversity.name}
                </h1>
                <div className="mt-2 flex items-center space-x-4">
                  <div className="flex items-center">
                    <MapPin className="mr-1 h-4 w-4 text-gray-500" />
                    <span className="text-gray-600">{selectedUniversity.city}</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="mr-1 h-4 w-4 text-yellow-500" />
                    <div className="text-2xl font-bold text-[#2db2b8]">
                      {selectedUniversity.rating}
                    </div>
                  </div>
                  <Badge
                    className="border-blue-200 bg-blue-50 text-[#2db2b8]"
                    variant="outline"
                  >
                    {selectedUniversity.type}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {selectedUniversity.employmentRate}%
                </div>
                <p className="text-sm text-gray-500">zatrudnialność</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column */}
            <div className="space-y-6 lg:col-span-2">
              {/* Description */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">O uczelni</h2>
                <p className="mb-4 leading-relaxed text-gray-700">
                  {selectedUniversity.description}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Rok założenia:</span>
                    <span className="ml-2 font-medium">
                      {selectedUniversity.foundedYear}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Liczba studentów:</span>
                    <span className="ml-2 font-medium">
                      {selectedUniversity.studentsCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Faculties */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Wydziały</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {selectedUniversity.faculties.map((faculty, index) => (
                    <div
                      className="flex items-center border border-gray-200 bg-gray-50 p-3"
                      key={index}
                    >
                      <GraduationCap className="mr-2 h-4 w-4 flex-shrink-0 text-[#2bcaca]" />
                      <span className="text-sm text-gray-700">{faculty}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Popular Fields */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Popularne kierunki</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedUniversity.popularFields.map((field, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm"
                      key={index}
                      variant="outline"
                    >
                      {field}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Advantages */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Zalety uczelni</h2>
                <div className="space-y-3">
                  {selectedUniversity.advantages.map((advantage, index) => (
                    <div
                      className="flex items-start border border-gray-200 bg-gray-50 p-3"
                      key={index}
                    >
                      <Award className="mt-0.5 mr-2 h-4 w-4 flex-shrink-0 text-[#2bcaca]" />
                      <span className="text-sm text-gray-700">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scholarships */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Stypendia</h2>
                <div className="space-y-2">
                  {selectedUniversity.scholarships.map((scholarship, index) => (
                    <div
                      className="flex items-center border border-gray-200 bg-gray-50 p-2"
                      key={index}
                    >
                      <DollarSign className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-700">{scholarship}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Informacje</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DollarSign className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Czesne</span>
                    </div>
                    <span className="text-sm font-medium">
                      {selectedUniversity.tuitionFee}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Akademik</span>
                    </div>
                    <span className="text-sm font-medium">
                      {selectedUniversity.dormitoryAvailable ? 'Dostępny' : 'Niedostępny'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Globe className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">
                        Programy międzynarodowe
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      {selectedUniversity.internationalPrograms ? 'Tak' : 'Nie'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <TrendingUp className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Zatrudnialność</span>
                    </div>
                    <span className="font-medium">
                      {selectedUniversity.employmentRate}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Kontakt</h2>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone className="mr-2 h-4 w-4 text-[#2bcaca]" />
                    <span className="text-sm text-gray-700">
                      {selectedUniversity.contact.phone}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="mr-2 h-4 w-4 text-[#2bcaca]" />
                    <span className="text-sm text-gray-700">
                      {selectedUniversity.contact.email}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="mr-2 h-4 w-4 text-[#2bcaca]" />
                    <span className="text-sm text-gray-700">
                      {selectedUniversity.contact.website}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Działania</h2>
                <div className="space-y-3">
                  <Button className="w-full bg-[#2bcaca] text-white hover:bg-[#229999] rounded-sm">
                    <Heart className="mr-2 h-4 w-4" />
                    Dodaj do ulubionych
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Odwiedź stronę
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <MapPin className="mr-2 h-4 w-4" />
                    Zobacz lokalizację
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Baza uczelni</h1>
          <p className="text-gray-600">
            Znajdź idealną uczelnię dla siebie spośród najlepszych w Polsce
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  className="pl-10 border-gray-200"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Szukaj uczelni lub miasta..."
                  value={searchTerm}
                />
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex gap-2">
              {types.map((type) => (
                <Button
                  className={cn(
                    selectedType === type
                      ? 'bg-[#2bcaca] text-white hover:bg-[#229999]'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  key={type}
                  onClick={() => setSelectedType(type)}
                  size="sm"
                  variant={selectedType === type ? 'default' : 'outline'}
                >
                  {type}
                </Button>
              ))}
            </div>

            {/* Region Filter */}
            <div className="flex flex-wrap gap-2">
              {regions.map((region) => (
                <Button
                  className={cn(
                    selectedRegion === region
                      ? 'bg-[#2bcaca] text-white hover:bg-[#229999]'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  key={region}
                  onClick={() => setSelectedRegion(region)}
                  size="sm"
                  variant={selectedRegion === region ? 'default' : 'outline'}
                >
                  {region}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredUniversities.map((university) => (
            <div
              className="cursor-pointer border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md flex flex-col h-full"
              key={university.id}
              onClick={() => setSelectedUniversity(university)}
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="mb-1 font-semibold text-gray-900">{university.name}</h3>
                  <p className="text-sm text-gray-500">{university.type}</p>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-lg font-bold text-[#2bcaca]">
                    {university.rating}
                  </div>
                  <p className="text-xs text-gray-500">ocena</p>
                </div>
              </div>

              {/* Description */}
              <p className="mb-4 line-clamp-3 text-sm text-gray-700 flex-grow">
                {university.description}
              </p>

              {/* Stats */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Miasto:</span>
                  <span className="font-medium">{university.city}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Zatrudnienie:</span>
                  <span className="font-medium">{university.employmentRate}%</span>
                </div>
              </div>

              {/* Popular Fields Preview */}
              <div className="mb-4">
                <p className="mb-2 text-xs text-gray-500">Popularne kierunki:</p>
                <div className="flex flex-wrap gap-1">
                  {university.popularFields.slice(0, 3).map((field, index) => (
                    <Badge className="text-xs" key={index} size="sm" variant="outline">
                      {field}
                    </Badge>
                  ))}
                  {university.popularFields.length > 3 && (
                    <Badge className="text-xs" size="sm" variant="outline">
                      +{university.popularFields.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Action */}
              <Button className="w-full rounded-sm mt-auto" size="sm" variant="outline">
                <School className="mr-2 h-4 w-4" />
                Zobacz szczegóły
              </Button>
            </div>
          ))}
        </div>

        {filteredUniversities.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              Nie znaleziono uczelni spełniających kryteria wyszukiwania.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
